import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Post from 'flarum/common/models/Post';
import Discussion from 'flarum/common/models/Discussion';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import Rosetta, { browserLanguage, normalizeTag } from './Rosetta';
import { watchPages } from './pageText';
export { Rosetta };
app.initializers.add('ernestdefoe-rosetta', () => {
  // The language comes from the browser: no picker.
  extend(HeaderSecondary.prototype, 'items', items => {
    if (app.forum.attribute('rosetta')) items.remove('locale');
  });
  const rosetta = new Rosetta();
  app.rosetta = rosetta;

  // The forum's settings arrive after initializers run. `rosetta` is the
  // forum's default language, or empty without a key to translate with.
  // A page in another language is in the reader's own (from their browser,
  // or one they picked), and what members wrote follows it; otherwise the
  // browser's language, whose interface may still be on its way.
  app.beforeMount(() => {
    const fallback = app.forum.attribute('rosetta');
    if (!fallback) return;
    const page = document.documentElement.lang;
    const target = page && page.split('-')[0] !== fallback.split('-')[0] ? normalizeTag(page) : browserLanguage();
    if (!target) return;
    rosetta.start(target);
    watchPages(rosetta);
  });

  // Tag names and descriptions (flarum/tags).
  const Tag = app.store.models['tags'];
  if (Tag) {
    override(Tag.prototype, 'name', function (original) {
      const name = original();
      return name && this.id() ? rosetta.text('tag:' + this.id(), name) : name;
    });
    override(Tag.prototype, 'description', function (original) {
      const description = original();
      return description && this.id() ? rosetta.text('tagdesc:' + this.id(), description) : description;
    });
  }

  // Posts. The model's own getter, so every place a post is drawn (the
  // discussion, search results, a reply that arrives live) gets it.
  override(Post.prototype, 'contentHtml', function (original) {
    const html = original();
    return html && this.contentType() === 'comment' && this.id() ? rosetta.html('post:' + this.id(), html) : html;
  });

  // Private messages: flarum/messages, and Group Messages on top of it.
  const DialogMessage = app.store.models['dialog-messages'];
  if (DialogMessage) {
    override(DialogMessage.prototype, 'contentHtml', function (original) {
      const html = original();
      return html && this.id() ? rosetta.html('message:' + this.id(), html) : html;
    });
  }

  // Titles, everywhere they are shown (lists, breadcrumbs, search, the
  // browser tab, a theme's own widgets), except the rename box, which
  // edits the title as written.
  override(Discussion.prototype, 'title', function (original) {
    const title = original();
    return !rosetta.raw && title && this.id() ? rosetta.text('discussion:' + this.id(), title) : title;
  });
  override(DiscussionControls, 'renameAction', function (original) {
    rosetta.raw = true;
    try {
      return original();
    } finally {
      rosetta.raw = false;
    }
  });

  // Parley chat, through the seam it leaves for a translator. Parley sets
  // itself up as the forum mounts (signed-in members only), so this runs
  // just after it.
  extend(app, 'mount', () => {
    const parley = app.parley;
    if (!parley || !rosetta.enabled) return;
    parley.translate = msg => {
      // A message still sending has no id yet.
      if (typeof msg.id !== 'number') return null;
      const key = 'parley:' + msg.id;
      const src = msg.body || '';
      const answer = rosetta.answer(key, src);
      if (!answer) return null;
      if (!answer.text) return {
        body: src,
        lang: answer.from,
        dir: answer.dir
      };
      const original = rosetta.showingOriginal(key);
      const name = rosetta.languageName(answer.from);
      return {
        body: original ? src : answer.text,
        lang: original ? answer.from : rosetta.target,
        dir: original ? answer.dir : rosetta.dir,
        note: m('div.Rosetta-note', {
          dir: rosetta.dir,
          lang: rosetta.target
        }, m('button.Rosetta-toggle[type=button]', {
          onclick: e => {
            // The bubble opens its menu on a click; this one is ours.
            e.stopPropagation();
            rosetta.toggle(key);
          }
        }, [m('i.fas.fa-language', {
          'aria-hidden': 'true'
        }), ' ', (name ? name + ' · ' : '') + (original ? rosetta.ui.show_translation : rosetta.ui.show_original)]))
      };
    };
  });
},
// After the extensions whose content this translates have set up.
-100);